import { FC, useCallback, useMemo } from "react";
import { OrderReviewLineItemDto } from "../../../../dtos";
import { DataTableColumn, StandardListViewV2 } from "../../../CoreLib/library";
import { triaxReferenceNumberColumnKeySuffixes } from '../../../../constants/triaxReferenceNumberColumnKeySuffixes';
import { BilledToType } from "../../../../dtos/enums/BilledToType";
import { formatCurrency } from "../../../../util";
import { Box, Button, Divider, Grid } from "@mui/material";
import { AddCircle } from "@mui/icons-material";
import { useGetOrderReviewLineItemsQuery } from "../../../../store/generated/generatedApi";
export interface IOrderReviewLineItemSelectListProps {
    orderReviewId: string;
    onSelect: (orderReviewLineItem: OrderReviewLineItemDto) => void;
    onAddNewLineItem: () => void;
}

export const OrderReviewLineItemSelectList: FC<IOrderReviewLineItemSelectListProps> = ({ onSelect, orderReviewId, onAddNewLineItem }) => {
    const tableColumns: DataTableColumn<OrderReviewLineItemDto>[] = useMemo(() => {
        const getMaterialPriceValue = (row: OrderReviewLineItemDto) => {
            if (row.lineItem?.isBilledTo === BilledToType.Customer || row.lineItem?.isBilledTo === BilledToType.Custom) {
                return 'BTC';
            }
            return row.lineItem?.rate !== null ? formatCurrency(row.lineItem?.rate) : '';
        };

        let columns: DataTableColumn<OrderReviewLineItemDto>[] = [
            { key: 'status', label: 'Status', sortKey: 'STATUS', unsorted: true, width: '90px', fieldMapper: (row) => (row.canceledOn ? 'Canceled' : '') },
            { key: 'description', label: 'Description', sortKey: 'DESCRIPTION', unsorted: true, fieldMapper: (row) => row.description },
            { key: 'site', label: 'Site', sortKey: 'SITE', unsorted: true, fieldMapper: (row) => row.site?.name ?? '' },
            { key: 'type', label: 'Type', sortKey: 'TYPE', unsorted: true, fieldMapper: (row) => row.lineItem?.type },
            { key: 'region', label: 'Region', sortKey: 'REGION', unsorted: true, fieldMapper: (row) => row.lineItem?.region },
            { key: 'equipmentType', label: 'Equip. Type', sortKey: 'EQUIPMENT_TYPE', width: '90px', unsorted: true, fieldMapper: (row) => row.equipmentType?.type },
            { key: 'unitOfMeasure', label: 'Units', sortKey: 'UNIT', align: 'right', unsorted: true, fieldMapper: (row) => row.lineItem?.unitOfMeasure },
            {
                key: 'driverRate',
                label: 'Driver Rate',
                sortKey: 'DRIVER_RATE',
                align: 'right',
                unsorted: true,
                fieldMapper: (row) => (row.billingRates?.driverRate !== null ? formatCurrency(row.billingRates?.driverRate) : ''),
            },
            {
                key: 'brokerRate',
                label: 'Broker Rate',
                sortKey: 'BROKER_RATE',
                align: 'right',
                unsorted: true,
                fieldMapper: (row) => (row.billingRates?.brokerRate !== null ? formatCurrency(row.billingRates?.brokerRate) : ''),
            },
            {
                key: 'rate',
                label: 'Material Rate',
                sortKey: 'MATERIAL_PRICE_VALUE',
                unsorted: true,
                align: 'right',
                fieldMapper: (row) => getMaterialPriceValue(row),
            },
            {
                key: 'haulingRate',
                label: 'Hauling Rate',
                sortKey: 'HAULING_RATE',
                unsorted: true,
                align: 'right',
                fieldMapper: (row) => (row.billingRates?.haulingRate !== null ? formatCurrency(row.billingRates?.haulingRate) : ''),
            },
            {
                key: 'quoteRate',
                label: 'Quote Rate',
                sortKey: 'QUOTE_RATE',
                unsorted: true,
                align: 'right',
                fieldMapper: (row) => (row.billingRates?.quoteRate !== null ? formatCurrency(row.billingRates?.quoteRate) : ''),
            }
        ];
        return columns;
    }, []);

    const handleSelect = useCallback((__: string, record: OrderReviewLineItemDto) => {
        if (record.canceledOn) {
            return;
        }
        onSelect(record);
    }, [onSelect]);

    const customRowStyling = useCallback((row: any) => {
        const lineItem = row as OrderReviewLineItemDto;
        return lineItem.canceledOn ? { opacity: 0.5, bgcolor: 'action.disabledBackground' } : {};
    }, []);

    return (
        <Box sx={{ height: '60vh', overflow: 'auto', width: '100%' }}>
            <Grid container direction='column' spacing={1}>
                <Grid item>
                    <StandardListViewV2
                        referenceNumberColumnKeySuffixes={triaxReferenceNumberColumnKeySuffixes}
                        enableMultiSort
                        headerIcon={<></>}
                        headerTitle={''}
                        breadCrumbs={[]}
                        isPaginationVisible={false}
                        getDataQuery={useGetOrderReviewLineItemsQuery}
                        additionalQueryParameters={orderReviewId ? { orderReviewId: orderReviewId } : undefined}
                        isShowActiveToggleVisible={false}
                        tableColumns={tableColumns}
                        entityNameSingular='Order Review Line Item'
                        editIcon={<AddCircle color='primary'/>}
                        editToolTip="Select Order Review Line Item"
                        permissionName='review'
                        disableSelection
                        customRowStyling={customRowStyling}
                        handleEdit={handleSelect}
                        hideSearch
                        hideMenu
                        hideAddButtonDivider
                        paginationOnTop={true}
                        stickyTableHeader={false}
                        maxTableHeight={'40vh'}
                    />
                </Grid>
                <Grid item>
                    <Divider>
                        OR
                    </Divider>
                </Grid>
                <Grid item>
                    <Button variant='contained' onClick={onAddNewLineItem} fullWidth>
                        Add New Line Item
                    </Button>
                </Grid>
            </Grid>
        </Box>
    );
};